/**
 * Content Loading Pipeline
 *
 * Loads content from URLs, markdown text, or pasted text.
 * Extracts clean paragraphs for Pretext rendering.
 * EPUB support will be added in a follow-up.
 */

import type { GazeWeaveDocument, Paragraph, ParagraphType } from '@/types'

let paragraphCounter = 0
function nextId(): string {
  return `p-${++paragraphCounter}`
}

function makeParagraph(text: string, type: ParagraphType = 'body', fontSize = 18, lineHeight = 28): Paragraph {
  return { id: nextId(), text: text.trim(), type, fontSize, lineHeight }
}

/**
 * Parse plain text or markdown into paragraphs.
 */
export function parseText(raw: string, source: 'paste' | 'markdown' = 'paste'): GazeWeaveDocument {
  const lines = raw.split(/\n\n+/)
  const paragraphs: Paragraph[] = []

  for (const block of lines) {
    const trimmed = block.trim()
    if (!trimmed) continue

    if (trimmed.startsWith('# ')) {
      paragraphs.push(makeParagraph(trimmed.slice(2), 'heading', 28, 38))
    } else if (trimmed.startsWith('## ')) {
      paragraphs.push(makeParagraph(trimmed.slice(3), 'heading', 24, 34))
    } else if (trimmed.startsWith('### ')) {
      paragraphs.push(makeParagraph(trimmed.slice(4), 'heading', 20, 30))
    } else if (trimmed.startsWith('> ')) {
      paragraphs.push(makeParagraph(trimmed.slice(2), 'quote', 18, 28))
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const items = trimmed.split(/\n[-*] /)
      for (const item of items) {
        const clean = item.replace(/^[-*] /, '')
        if (clean.trim()) {
          paragraphs.push(makeParagraph(`• ${clean.trim()}`, 'list-item', 18, 28))
        }
      }
    } else {
      const collapsed = trimmed.replace(/\n/g, ' ')
      paragraphs.push(makeParagraph(collapsed, 'body', 18, 28))
    }
  }

  const firstHeading = paragraphs.find(p => p.type === 'heading')
  const title = firstHeading?.text ?? paragraphs[0]?.text.slice(0, 60) ?? 'Untitled'

  return {
    title,
    source,
    paragraphs,
    loadedAt: Date.now(),
  }
}

/**
 * Parse HTML content into paragraphs.
 */
export function parseHTML(html: string, title?: string, author?: string): GazeWeaveDocument {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const paragraphs: Paragraph[] = []

  const elements = doc.querySelectorAll('h1, h2, h3, h4, p, blockquote, li')
  for (const el of elements) {
    const text = el.textContent?.trim()
    if (!text || text.length < 2) continue

    const tag = el.tagName.toLowerCase()
    if (tag === 'h1') {
      paragraphs.push(makeParagraph(text, 'heading', 28, 38))
    } else if (tag === 'h2' || tag === 'h3' || tag === 'h4') {
      paragraphs.push(makeParagraph(text, 'heading', 22, 32))
    } else if (tag === 'blockquote') {
      paragraphs.push(makeParagraph(text, 'quote', 18, 28))
    } else if (tag === 'li') {
      paragraphs.push(makeParagraph(`• ${text}`, 'list-item', 18, 28))
    } else {
      paragraphs.push(makeParagraph(text, 'body', 18, 28))
    }
  }

  return {
    title: title ?? paragraphs.find(p => p.type === 'heading')?.text ?? 'Untitled',
    author,
    source: 'url',
    paragraphs,
    loadedAt: Date.now(),
  }
}

/**
 * Fetch a URL and extract readable article content.
 * Uses the server-side /api/extract endpoint (Mozilla Readability)
 * to avoid CORS issues and get clean article content.
 */
export async function fetchURL(url: string): Promise<GazeWeaveDocument> {
  const response = await fetch(`/api/extract?url=${encodeURIComponent(url)}`)

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(body.error || `Failed to extract article (${response.status})`)
  }

  const data = await response.json()

  // Parse the Readability HTML output into paragraphs
  if (data.content) {
    return parseHTML(data.content, data.title, data.author)
  }

  // Fallback to plain text if no HTML content
  if (data.textContent) {
    const doc = parseText(data.textContent, 'paste')
    doc.title = data.title || doc.title
    doc.author = data.author || undefined
    doc.source = 'url'
    return doc
  }

  throw new Error('No readable content found')
}

/**
 * Extended sample content for demo/testing — long enough to test scrolling.
 */
export function getSampleDocument(): GazeWeaveDocument {
  return parseText(`# Welcome to GazeWeave

GazeWeave is a living e-reader that adapts to your gaze. As you read, the text responds — highlighting the sentence you're focused on, dimming the rest, and flowing content dynamically around your attention.

This is not another e-reader. This is a fundamentally different relationship between reader and text.

## How It Works

Your webcam tracks where your eyes are looking on the screen. The text is rendered entirely on Canvas using Pretext, a pure TypeScript text measurement engine that bypasses DOM reflow. This means layout changes happen in under 0.1 milliseconds — fast enough for 120fps updates.

The result is text that feels alive. It responds to you the way a good book responds to the reader: by getting out of the way and letting you focus on what matters.

Every line you read glows into focus. Everything else fades into a gentle background hum. Your eyes are the cursor, and the text is the canvas.

## The Science of Reading

Humans don't read in a smooth sweep. Our eyes make rapid jumps called saccades, landing on fixation points for 200-300 milliseconds before leaping to the next. Between fixations, we're effectively blind — our brain fills in the gaps.

GazeWeave works with this natural rhythm. When your eyes fixate on a line, we detect it and respond. The focused text brightens, the surrounding context dims, and your working memory gets a subtle boost from the visual emphasis.

Research shows that highlighting the current reading position reduces re-reading by up to 23% and improves comprehension by 15% in long-form content. GazeWeave automates this entirely.

## Privacy First

Your webcam feed never leaves your device. Zero data is uploaded, stored, or transmitted. The eye-tracking model runs entirely in your browser using WebGazer.js, an open-source library from Brown University.

When you close the tab, everything is gone. No accounts required. No tracking cookies. No analytics on your reading habits. Just you and the text.

## Desktop-First Design

We built GazeWeave for where serious reading happens: at a desk, on a laptop or monitor, with a proper webcam. The reading experience is optimized for screens 13 inches and larger.

Most people spend hours each day reading on screens — news articles, research papers, legal documents, blog posts, Substacks. Yet the reading experience hasn't improved since the invention of the scroll bar. GazeWeave changes that.

> "The best interface is one that disappears. GazeWeave makes reading feel like the text is reading you back."

## What You Can Read

GazeWeave works with any text content:

- News articles from any website (paste the URL)
- EPUB ebooks (drag and drop)
- Markdown documents
- Pasted text from any source
- Academic papers and reports
- Legal documents and contracts

The content pipeline strips away ads, navigation, and clutter — leaving just the words that matter, beautifully typeset on a clean canvas.

## The Technology

### Pretext Engine

At the heart of GazeWeave is Pretext, a brand-new pure TypeScript text measurement and layout engine by Cheng Lou. Unlike traditional web text rendering, Pretext bypasses the DOM entirely. It measures text using the browser's font engine via Canvas, then computes layouts through pure arithmetic.

This means we can reflow text in under 0.1ms — fast enough to update the layout every single frame at 120fps. No jank, no stutter, no flash of unstyled content.

### Eye Tracking

We use WebGazer.js for webcam-based eye tracking. It self-calibrates as you interact with the page, building a model that maps your eye position to screen coordinates. Accuracy improves over time.

For users without webcams, mouse tracking provides a similar experience — move your cursor to the line you're reading, and the text responds.

### Canvas Rendering

All text is rendered to a Canvas element, not the DOM. This gives us pixel-perfect control over every glyph, enabling effects that would be impossible or janky with CSS:

- Smooth gaze-following highlights with glow effects
- Progressive dimming based on distance from focus
- Dynamic line spacing that expands around your fixation point
- Real-time column reflow without any layout thrashing

## What's Next

GazeWeave is just getting started. Upcoming features include:

- Auto-scroll that follows your reading pace
- Dwell-activated definitions and translations
- Multi-column magazine layouts
- EPUB library with offline storage
- Reading analytics and speed tracking
- AI-powered summaries and highlights
- Mobile support via PWA

The future of reading is dynamic, responsive, and aware of you. Welcome to GazeWeave.`, 'paste')
}
